import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Brain,
  Database,
  BarChart3,
  Globe,
  ArrowRight,
  Eye,
  Settings,
  TrendingUp,
  Target,
  BookOpen,
  LogOut,
} from "lucide-react";

function HeroSection({ onGetStarted }: { onGetStarted: () => void }) {
  return (
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-[#063F6B]">
      {/* Layered depth glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[700px] bg-[#124C77]/40 rounded-full blur-[140px]" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#EAA059]/[0.05] rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        {/* Accent line */}
        <motion.div
          className="w-12 h-[2px] bg-[#EAA059] mx-auto mb-8"
          initial={{ width: 0 }}
          animate={{ width: 48 }}
          transition={{ delay: 0.1, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        />

        <motion.h1
          className="text-4xl md:text-6xl lg:text-7xl font-semibold text-white mb-6 leading-[1.1] tracking-tight"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        >
          Evaluate AI Vendors with Confidence
        </motion.h1>

        <motion.p
          className="text-lg md:text-xl text-slate-300/90 mb-8 max-w-2xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        >
          A structured framework for evaluating AI vendors — so you can
          distinguish real capability from marketing, and make decisions that
          build long-term organizational strength.
        </motion.p>

        <motion.div
          className="flex items-center justify-center gap-4"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <button
            onClick={onGetStarted}
            className="group px-9 py-4 bg-gradient-to-r from-[#d4893e] to-[#c07830] hover:from-[#c07830] hover:to-[#b06a25] text-white text-lg font-medium rounded-full transition-all duration-300 shadow-lg shadow-[#EAA059]/20 hover:shadow-xl hover:shadow-[#EAA059]/30 inline-flex items-center gap-2"
          >
            Get Started
            <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform duration-200" />
          </button>
          <a
            href="#how-it-works"
            className="px-9 py-4 text-white text-lg font-medium rounded-full border border-white/20 hover:border-white/40 hover:bg-white/[0.06] transition-all duration-300 inline-flex items-center gap-2"
          >
            See How It Works ↓
          </a>
        </motion.div>
      </div>
    </section>
  );
}

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

function FeatureCard({ icon, title, description, delay }: FeatureCardProps) {
  return (
    <motion.div
      className="group p-6 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 hover:-translate-y-1 transition-all duration-300"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ delay, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="w-12 h-12 rounded-xl bg-[#EAA059]/15 border border-[#EAA059]/20 backdrop-blur-sm flex items-center justify-center mb-5 shadow-sm group-hover:shadow-md transition-shadow duration-300">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-[#063F6B] mb-3 tracking-tight">{title}</h3>
      <p className="text-gray-500 leading-relaxed text-sm">{description}</p>
    </motion.div>
  );
}

function HowItWorksSection() {
  const features = [
    {
      icon: <Globe className="w-6 h-6" style={{ color: '#D4893E' }} />,
      title: "Enter a URL",
      description:
        "Provide a vendor's website address and the system handles discovery, scraping, and evaluation automatically.",
    },
    {
      icon: <Database className="w-6 h-6" style={{ color: '#D4893E' }} />,
      title: "AI Powered Analysis",
      description:
        "The system crawls vendor websites, extracting information about products, pricing, and capabilities so you don't have to.",
    },
    {
      icon: <Brain className="w-6 h-6" style={{ color: '#D4893E' }} />,
      title: "Strategic Framework",
      description:
        "Vendors are assessed across six criteria — SEE, CHANGE, ADAPT, USE, LEARN, and EXIT — to give a well-rounded picture.",
    },
    {
      icon: <BarChart3 className="w-6 h-6" style={{ color: '#D4893E' }} />,
      title: "Structured Reports",
      description:
        "Results are organized into readable reports with scores, summaries, and context for each evaluation area.",
    },
  ];

  return (
    <section id="how-it-works" className="py-16 bg-[#F9F7F5]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="w-10 h-[2px] bg-[#EAA059] mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-semibold text-[#063F6B] mb-4 tracking-tight">
            How the Tool Works
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            A structured way to research and compare AI vendors using publicly
            available information.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              delay={index * 0.08}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

const CRITERIA = [
  {
    icon: Eye,
    title: "SEE",
    subtitle: "Transparency & Visibility",
    question: "Can you see how the AI works?",
    description:
      "Evaluates whether the vendor exposes the prompts, models, and architecture being used — or operates as a black box.",
    color: "#4DA6D4",
    colorDark: "#3B8DB8",
  },
  {
    icon: Settings,
    title: "CHANGE",
    subtitle: "Customizability & Control",
    question: "Can you modify AI behavior?",
    description:
      "Assesses your ability to adjust prompts, select models, and configure the system to fit your organization's needs.",
    color: "#9B4FD4",
    colorDark: "#7E3AB5",
  },
  {
    icon: TrendingUp,
    title: "ADAPT",
    subtitle: "Vendor Agility & Innovation",
    question: "Does the vendor keep pace?",
    description:
      "Measures how quickly the vendor adopts frontier AI capabilities and whether you're locked to a lagging update cycle.",
    color: "#5BBFB5",
    colorDark: "#45A199",
  },
  {
    icon: Target,
    title: "USE",
    subtitle: "Genuine Utility & Value",
    question: "Is the tool actually useful?",
    description:
      "Determines whether the tool provides real, measurable value — or merely the impression of usefulness.",
    color: "#C47B9F",
    colorDark: "#A86485",
  },
  {
    icon: BookOpen,
    title: "LEARN",
    subtitle: "Transferable Knowledge",
    question: "Do you build portable AI skills?",
    description:
      "Evaluates whether using the tool builds generalizable AI capabilities across your organization, or only teaches vendor-specific workflows.",
    color: "#5C6BC0",
    colorDark: "#4A57A3",
  },
  {
    icon: LogOut,
    title: "EXIT",
    subtitle: "Portability & Migration",
    question: "Can you leave cleanly?",
    description:
      "Assesses the difficulty of transitioning away — including data portability, format lock-in, and switching costs.",
    color: "#C4785A",
    colorDark: "#A86245",
  },
];

function CriteriaSection() {
  return (
    <section className="py-16 bg-[#063F6B] relative overflow-hidden">
      {/* Depth glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[700px] bg-[#124C77]/35 rounded-full blur-[140px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section header */}
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="w-10 h-[2px] bg-[#EAA059] mx-auto mb-5" />
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-3 tracking-tight">
            The 6-Criterion Framework
          </h2>
          <p className="text-lg text-[#EAA059] max-w-2xl mx-auto">
            Every vendor is evaluated against six dimensions that assess both
            immediate utility and long-term organizational capacity building.
          </p>
        </motion.div>

        {/* Criteria grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5 mb-12">
          {CRITERIA.map((criterion, index) => {
            const Icon = criterion.icon;
            return (
              <motion.div
                key={criterion.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: index * 0.08, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                whileHover={{ y: -4, transition: { duration: 0.25 } }}
                className="group p-6 lg:p-7 rounded-2xl bg-white/[0.05] border border-white/[0.08] hover:bg-white/[0.08] transition-all duration-300"
                style={{ ["--criterion-color" as string]: criterion.color }}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md transition-shadow duration-300"
                    style={{ background: `linear-gradient(135deg, ${criterion.color}, ${criterion.colorDark})` }}
                  >
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white tracking-tight">{criterion.title}</h3>
                    <p className="text-sm font-medium" style={{ color: '#EAA059' }}>{criterion.subtitle}</p>
                  </div>
                </div>
                <p className="text-white/90 font-medium text-[15px] mb-1.5">
                  {criterion.question}
                </p>
                <p className="text-slate-400 leading-relaxed text-sm">
                  {criterion.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Paramount question */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-center"
        >
          <div className="inline-block rounded-2xl px-7 py-6 border border-white/[0.08] bg-white/[0.03] backdrop-blur-sm max-w-3xl">
            <p className="text-base sm:text-lg text-slate-300/90 leading-relaxed">
              <span className="text-[#EAA059] font-semibold">The paramount question:</span>{" "}
              Does this tool support or delay your organization's capacity to work effectively with AI?
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function CTASection({ onGetStarted }: { onGetStarted: () => void }) {
  return (
    <section className="py-16 bg-[#063F6B] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-[#124C77]/30 rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="w-10 h-[2px] bg-[#EAA059] mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4 tracking-tight">
            Start exploring your AI vendor landscape
          </h2>
          <p className="text-lg text-slate-300/80 mb-8 max-w-xl mx-auto">
            Enter a vendor URL and get a structured evaluation in minutes.
          </p>

          <button
            onClick={onGetStarted}
            className="group px-9 py-4 bg-gradient-to-r from-[#d4893e] to-[#c07830] hover:from-[#c07830] hover:to-[#b06a25] text-white text-lg font-medium rounded-full transition-all duration-300 shadow-lg shadow-[#EAA059]/20 hover:shadow-xl hover:shadow-[#EAA059]/30 inline-flex items-center gap-2"
          >
            Get Started
            <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform duration-200" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#042a4d] py-12">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img
              src="/logo-light.png"
              alt="Feedforward AI"
              className="h-8 w-auto"
            />
          </div>
          <p className="text-slate-500 text-sm">
            &copy; {new Date().getFullYear()} Feedforward AI. All rights
            reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate("/analysis");
    } else {
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen">
      <HeroSection onGetStarted={handleGetStarted} />
      <HowItWorksSection />
      <CriteriaSection />
      <CTASection onGetStarted={handleGetStarted} />
      <Footer />
    </div>
  );
}
